import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useReducedMotion } from "framer-motion";
import { StageContext, type SceneMeta, type StageValue } from "./context";

/**
 * Fallback for browsers without `scrollend`: how long the page must sit still
 * before a scroll counts as settled. Snap animations emit a scroll event every
 * frame, so a gap this long means the snap has finished.
 */
const SETTLE_MS = 140;
/** Duration of a scene-to-scene glide. */
const GLIDE_MS = 850;
/** A wheel pause longer than this starts a new gesture. */
const GESTURE_GAP_MS = 200;

const easeInOutCubic = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2);
const mean = (xs: number[]) => xs.reduce((a, b) => a + b, 0) / Math.max(1, xs.length);

const byDocumentOrder = (els: Map<string, HTMLElement>) => (a: SceneMeta, b: SceneMeta) => {
  const ea = els.get(a.id);
  const eb = els.get(b.id);
  if (!ea || !eb) return 0;
  return ea.compareDocumentPosition(eb) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
};

/** True when something between `target` and the page can still scroll in `deltaY`'s direction. */
function scrollsWithin(target: EventTarget | null, deltaY: number) {
  for (let el = target instanceof Element ? target : null; el && el !== document.body; el = el.parentElement) {
    const { overflowY } = getComputedStyle(el);
    if (overflowY !== "auto" && overflowY !== "scroll") continue;
    if (deltaY > 0 ? el.scrollTop + el.clientHeight < el.scrollHeight - 1 : el.scrollTop > 0) return true;
  }
  return false;
}

/**
 * The landing page's scroll stage. While mounted it makes the document snap
 * one full-screen scene at a time, and it decides which scene has "arrived":
 * the one covering most of the viewport once scrolling has come to rest. A
 * scene plays its entrance only on arrival, i.e. after the snap lands, never
 * mid-scroll.
 *
 * Native snapping only advances once a wheel scroll passes half a screen, so a
 * gentle swipe would bounce back. The wheel is therefore steered by hand: one
 * gesture glides exactly one scene, and the inertia tail of a trackpad swipe
 * is ignored so a single swipe can't skip past a scene. Keyboard and touch
 * keep the browser's own snapping, which already moves one scene per press or
 * flick.
 */
export function SnapStage({ children }: { children: ReactNode }) {
  const reduce = useReducedMotion();
  const elements = useRef(new Map<string, HTMLElement>());
  const order = useRef<string[]>([]);
  const gliding = useRef(false);
  const glideFrame = useRef(0);
  const [scenes, setScenes] = useState<SceneMeta[]>([]);
  const [arrivedId, setArrivedId] = useState<string | null>(null);

  // Snap only while the landing page is mounted (see html.snap-stage).
  useLayoutEffect(() => {
    const root = document.documentElement;
    root.classList.add("snap-stage");
    return () => {
      // Leaving mid-glide must not keep scrolling the next page.
      cancelAnimationFrame(glideFrame.current);
      root.classList.remove("snap-stage", "snap-gliding");
    };
  }, []);

  const register = useCallback((meta: SceneMeta, el: HTMLElement) => {
    elements.current.set(meta.id, el);
    setScenes((prev) =>
      [...prev.filter((s) => s.id !== meta.id), meta].sort(byDocumentOrder(elements.current)),
    );
    return () => {
      elements.current.delete(meta.id);
      setScenes((prev) => prev.filter((s) => s.id !== meta.id));
    };
  }, []);

  useEffect(() => {
    order.current = scenes.map((s) => s.id);
  }, [scenes]);

  /** The scene covering the most of the viewport right now. */
  const covering = useCallback(() => {
    const vh = window.innerHeight;
    let best: string | null = null;
    let bestCover = 0;
    for (const [id, el] of elements.current) {
      const r = el.getBoundingClientRect();
      const cover = Math.min(r.bottom, vh) - Math.max(r.top, 0);
      if (cover > bestCover + 1) {
        best = id;
        bestCover = cover;
      }
    }
    return best;
  }, []);

  const settle = useCallback(() => {
    if (gliding.current) return;
    const id = covering();
    if (id) setArrivedId(id);
  }, [covering]);

  /** Eased scroll to a scene's top. Snapping is paused meanwhile or it would yank every frame. */
  const glideTo = useCallback(
    (id: string) => {
      const el = elements.current.get(id);
      if (!el) return;
      const root = document.documentElement;
      const from = window.scrollY;
      const to = Math.round(el.getBoundingClientRect().top + from);
      if (reduce || Math.abs(to - from) < 2) {
        el.scrollIntoView({ block: "start" });
        return;
      }
      gliding.current = true;
      root.classList.add("snap-gliding");
      const start = performance.now();
      const step = (now: number) => {
        const t = Math.min(1, (now - start) / GLIDE_MS);
        window.scrollTo(0, from + (to - from) * easeInOutCubic(t));
        if (t < 1) {
          glideFrame.current = requestAnimationFrame(step);
          return;
        }
        root.classList.remove("snap-gliding");
        gliding.current = false;
        settle();
      };
      cancelAnimationFrame(glideFrame.current);
      glideFrame.current = requestAnimationFrame(step);
    },
    [reduce, settle],
  );

  /**
   * Explicit navigation (in-page links, the pager): glide there and move focus
   * to the scene, so the next Tab and a screen reader's position follow the
   * view. The wheel only glides; it never moves focus.
   */
  const goTo = useCallback(
    (id: string) => {
      glideTo(id);
      elements.current.get(id)?.focus({ preventScroll: true });
    },
    [glideTo],
  );

  useEffect(() => {
    // Child effects run first, so every scene has registered by now. A shared
    // link like /#faq lands on its scene; the SPA renders too late for the
    // browser to have scrolled there itself.
    elements.current.get(window.location.hash.slice(1))?.scrollIntoView({ block: "start", behavior: "instant" });
    settle();
    let timer = 0;
    const onScroll = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(settle, SETTLE_MS);
    };
    const onScrollEnd = () => {
      window.clearTimeout(timer);
      settle();
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("scrollend", onScrollEnd);
    window.addEventListener("resize", onScroll);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("scrollend", onScrollEnd);
      window.removeEventListener("resize", onScroll);
    };
  }, [settle]);

  // In-page links (header, footer, scroll hint) glide like the wheel does.
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const link = e.target instanceof Element ? e.target.closest<HTMLAnchorElement>('a[href^="#"]') : null;
      const id = link ? link.hash.slice(1) : "";
      if (!elements.current.has(id)) return;
      e.preventDefault();
      window.history.replaceState(null, "", `#${id}`);
      goTo(id);
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, [goTo]);

  // Wheel / trackpad: one gesture, one scene.
  useEffect(() => {
    if (reduce) return;
    const recent: number[] = [];
    let lastWheel = 0;
    let travel = 0;
    let spent = false;
    let peak = 0;
    let decayed = false;

    const onWheel = (e: WheelEvent) => {
      if (e.ctrlKey || e.defaultPrevented) return;
      const unit = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? window.innerHeight : 1;
      const dy = e.deltaY * unit;
      if (Math.abs(dy) <= Math.abs(e.deltaX * unit)) return;

      // Track the gesture before anything is handed to native scrolling, so
      // its tail is still recognised as the same gesture afterwards.
      const now = performance.now();
      if (now - lastWheel > GESTURE_GAP_MS) {
        recent.length = 0;
        travel = 0;
        spent = false;
        peak = 0;
        decayed = false;
      }
      lastWheel = now;
      const mag = Math.abs(dy);
      recent.push(mag);
      if (recent.length > 60) recent.shift();
      if (mag > peak) peak = mag;
      else if (mag < peak * 0.4) decayed = true;

      // An inner scroller (the feedback textarea) or a scene taller than the
      // screen scrolls until its edge is reached. A gesture used for that is
      // spent: reaching the edge rests there rather than gliding on.
      if (!gliding.current && scrollsWithin(e.target, dy)) {
        spent = true;
        return;
      }
      const currentId = covering();
      const current = currentId ? elements.current.get(currentId) : undefined;
      if (!currentId || !current) return;
      const r = current.getBoundingClientRect();
      // Room left before the scene's edge meets the viewport's, in the wheel's direction.
      const room = dy > 0 ? r.bottom - window.innerHeight : r.top;
      if (!gliding.current && Math.abs(room) > 2 && Math.sign(room) === Math.sign(dy)) {
        // Scrolled by hand and clamped: a native wheel step can jump clean past
        // the edge into the next tall scene, which snapping would then accept.
        e.preventDefault();
        window.scrollBy({ top: dy > 0 ? Math.min(dy, room) : Math.max(dy, room), behavior: "instant" });
        spent = true;
        return;
      }

      e.preventDefault();
      travel += dy;
      if (gliding.current) return;

      // Each gesture buys one glide, once it has travelled far enough to be
      // deliberate. Everything after that, including a trackpad's long inertia
      // tail, is spent, unless the deltas surge again after having decayed: a
      // fresh swipe made before the tail died out. (Without the decay test the
      // ramp-up of the SAME swipe reads as a surge.)
      const surging =
        decayed &&
        recent.length >= 6 &&
        mag >= 12 &&
        mean(recent.slice(-3)) > 1.4 * mean(recent.slice(-12, -3));
      if (!(surging || (!spent && Math.abs(travel) >= 40))) return;
      spent = true;
      travel = 0;
      if (surging) {
        peak = mag;
        decayed = false;
      }

      const next = order.current[order.current.indexOf(currentId) + (dy > 0 ? 1 : -1)];
      if (next) glideTo(next);
    };

    window.addEventListener("wheel", onWheel, { passive: false });
    return () => window.removeEventListener("wheel", onWheel);
  }, [reduce, covering, glideTo]);

  const value = useMemo<StageValue>(
    () => ({ arrivedId, scenes, register, goTo }),
    [arrivedId, scenes, register, goTo],
  );

  return <StageContext.Provider value={value}>{children}</StageContext.Provider>;
}
